// Call transcript data layer (#6085 / #6232) — the app-session reads that
// survive the admin-only re-scope (ruling 2026-08-20): transcript viewing
// and metadata editing (Documents library), the pending-action count
// (strategy report panel), and the report-origin follow-up lifecycle
// (report viewer). Action extraction, review, and apply live in admin.

import { queryOptions, useMutation, useQueryClient } from '@tanstack/react-query';
import fetchWithAuth from './apis';
import { apiEndpoint } from './consts';

const BASE = `${apiEndpoint}/call-actions`;

// ---------------------------------------------------------------------------- types
export interface CallAttendee {
  user_id?: number;
  name?: string;
  email?: string;
}

/** A pulled action awaiting review (immutable extraction). */
export interface ExtractedAction {
  id: number;
  /** One origin per row: a call transcript, or a strategy doc run for the
   * report's confirm-marker follow-ups (#6232). */
  call_transcript_id: number | null;
  strategy_doc_run_id: number | null;
  category: string; // e.g. 'app_action' | 'open_question'
  action_type: string | null; // e.g. 'ADD_NEGATIVE_KEYWORD' | 'SET_BUDGET'
  platform: string | null; // e.g. 'google' | 'meta'
  target_scope: string | null;
  target_ref: string | null;
  summary: string | null;
  params: Record<string, unknown>;
  confidence: number;
  needs_clarification: boolean;
  source_quote: string;
  review_decision: string; // 'pending' | 'approved' | 'rejected' | 'skipped'
  /** Manual follow-up lifecycle (#6232): null = open, 'done' | 'dismissed'. */
  followup_status: string | null;
  /** Provenance from the transcript: speakers + timestamp, when labelled. */
  proposed_by: string | null;
  agreed_by: string | null;
  source_timestamp: string | null;
}

/** A saved transcript's metadata (detail header). */
export interface CallTranscript {
  id: number;
  business_id: number;
  business_name: string | null;
  organisation_id: number;
  title: string | null;
  source_filename: string | null;
  attendees: CallAttendee[] | null;
  source: string;
  occurred_at: string | null;
  created_at: string;
  default_currency: string | null; // org's ISO 4217 code, for budget display
}

export interface PendingExtractedAction extends ExtractedAction {
  transcript_title: string | null;
  transcript_created_at: string | null;
}

export interface CallTranscriptDetail {
  transcript: CallTranscript;
  counts: Record<string, number>;
  actions_by_category: Record<string, ExtractedAction[]>;
  /** A live strategy-doc run consumed this call — the build fork is done. */
  strategy_built: boolean;
}

export interface CallOrgUser {
  user_id: number;
  name: string;
  email: string;
}


// ---------------------------------------------------------------------------- reads
export async function getCallTranscript(transcriptId: number): Promise<CallTranscriptDetail> {
  const res = await fetchWithAuth(`${BASE}/transcripts/${transcriptId}`);
  if (!res.ok) throw new Error('Failed to load transcript');
  return res.json();
}

export async function getCallTranscriptText(transcriptId: number): Promise<{ transcript_id: number; text: string }> {
  const res = await fetchWithAuth(`${BASE}/transcripts/${transcriptId}/text`);
  if (!res.ok) throw new Error('Failed to load transcript text');
  return res.json();
}

export async function updateTranscript(
  transcriptId: number,
  body: { title?: string | null; attendee_user_ids?: number[] },
): Promise<CallTranscript> {
  const res = await fetchWithAuth(`${BASE}/transcripts/${transcriptId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error('Failed to update transcript');
  return res.json();
}
export async function listCallOrgUsers(businessId: number): Promise<CallOrgUser[]> {
  const res = await fetchWithAuth(`${BASE}/org-users?business_id=${businessId}`);
  if (!res.ok) throw new Error('Failed to load attendees');
  return res.json();
}


// --------------------------------------------------------------------- query options
export const callTranscriptQueryOptions = (transcriptId: number | undefined) =>
  queryOptions({
    queryKey: ['call-transcript', transcriptId],
    queryFn: () => getCallTranscript(transcriptId as number),
    enabled: !!transcriptId,
    staleTime: 5 * 60_000,
  });

export const callOrgUsersQueryOptions = (businessId: number | undefined) =>
  queryOptions({
    queryKey: ['call-org-users', businessId],
    queryFn: () => listCallOrgUsers(businessId as number),
    enabled: !!businessId,
    staleTime: 5 * 60_000,
  });

// ------------------------------------------------------------------------- mutations

// ------------------------------------------------------------------------- mutations
export function useUpdateTranscriptMutation(businessId: number, transcriptId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: { title?: string | null; attendee_user_ids?: number[] }) => updateTranscript(transcriptId, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['call-transcript', transcriptId] });
      queryClient.invalidateQueries({ queryKey: ['call-transcripts', businessId] });
    },
  });
}

/** Submit agree/disagree decisions, then refresh the transcript detail + the library. */

// ------------------------------------------------------- report follow-ups + counts
export async function listFollowups(businessId: number): Promise<PendingExtractedAction[]> {
  const res = await fetchWithAuth(`${BASE}/extracted-actions/followups?business_id=${businessId}`);
  if (!res.ok) throw new Error('Follow-ups could not be loaded');
  return res.json();
}

/** Mark a manual follow-up done or dismissed (or reopen it). */
export async function setFollowupStatus(
  actionId: number,
  status: 'done' | 'dismissed' | 'open',
): Promise<PendingExtractedAction> {
  const res = await fetchWithAuth(`${BASE}/extracted-actions/${actionId}/followup`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ status }),
  });
  if (!res.ok) throw new Error('The follow-up could not be updated');
  return res.json();
}

/** Every extracted action still awaiting a decision, across all the business's calls. */
export async function listPendingActions(businessId: number): Promise<PendingExtractedAction[]> {
  const res = await fetchWithAuth(`${BASE}/extracted-actions/pending?business_id=${businessId}`);
  if (!res.ok) throw new Error('Pending actions could not be loaded');
  return res.json();
}

